import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CreditCard, AlertTriangle, QrCode, Clock, Bell, User } from "lucide-react";
import { OperacoesNotificacoesSection } from "@/components/settings/OperacoesNotificacoesSection";

export function OperacoesTab({ restaurant, authAxios, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const [paymentAtTable, setPaymentAtTable] = useState(
    restaurant?.payment_at_table_enabled !== false
  );
  const [requireCustomerTable, setRequireCustomerTable] = useState(
    restaurant?.require_customer_info_table !== false  // default true
  );
  const [autoClose, setAutoClose] = useState(!!restaurant?.caixa_auto_close_enabled);
  const [autoConfirmReservations, setAutoConfirmReservations] = useState(!!restaurant?.reservation_auto_confirm);
  const [autoCloseHora, setAutoCloseHora] = useState(
    restaurant?.caixa_auto_close_hora ?? 5
  );
  const [reportEmail, setReportEmail] = useState(
    restaurant?.caixa_auto_close_email || restaurant?.report_email || ""
  );
  const [whatsappNumber, setWhatsappNumber] = useState(
    restaurant?.whatsapp_number || ""
  );

  const horaNum = parseInt(autoCloseHora, 10);
  const isMidnightHour = !isNaN(horaNum) && horaNum >= 0 && horaNum <= 4;

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.patch("/restaurant", {
        payment_at_table_enabled: paymentAtTable,
        require_customer_info_table: requireCustomerTable,
        caixa_auto_close_enabled: autoClose,
        caixa_auto_close_hora: horaNum,
        caixa_auto_close_email: reportEmail.trim() || null,
        report_email: reportEmail.trim() || null,
        whatsapp_number: whatsappNumber.trim() || null,
        reservation_auto_confirm: autoConfirmReservations,
      });
      toast.success("Configurações guardadas!");
      if (onUpdate) onUpdate();
    } catch {
      toast.error("Erro ao guardar configurações");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="operacoes-tab">
      <div>
        <h3 className="text-lg font-bold">Operações do Restaurante</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Configure o fluxo de pedidos, pagamentos e notificações do seu estabelecimento.
        </p>
      </div>

      {/* Payment at Table Toggle */}
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
              <CreditCard className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-sm">Pagamento na mesa</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Permite que clientes paguem após consumir (mesa / QR / garçom).
              </p>
            </div>
          </div>
          <Switch
            checked={paymentAtTable}
            onCheckedChange={setPaymentAtTable}
            data-testid="payment-at-table-toggle"
          />
        </div>

        {paymentAtTable ? (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 space-y-2">
            <p className="text-xs font-bold text-emerald-800">Modo ativo — Pagamento após consumo</p>
            <ul className="text-xs text-emerald-700 space-y-1 list-disc list-inside">
              <li>Pedidos entram imediatamente em preparação</li>
              <li>Cliente paga no final (mesa, QR ou garçom)</li>
              <li>QR de mesa disponível para o cliente acompanhar a conta</li>
              <li>Status do pedido: <span className="font-mono font-bold">pending → in_preparation → ready</span></li>
            </ul>
            <div className="flex items-center gap-2 mt-2">
              <QrCode className="w-4 h-4 text-emerald-600" />
              <span className="text-xs text-emerald-700 font-medium">QR de mesa ativado automaticamente</span>
            </div>
          </div>
        ) : (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <p className="text-xs font-bold text-amber-800">Modo pagamento antecipado</p>
            </div>
            <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
              <li>Pagamento obrigatório antes de enviar para cozinha</li>
              <li>Pedidos sem pagamento ficam bloqueados em <span className="font-mono font-bold">pending_payment</span></li>
              <li>Aviso exibido ao cliente: "Este restaurante exige pagamento antecipado"</li>
            </ul>
          </div>
        )}
      </div>

      {/* Dados do cliente na mesa (QR code) */}
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center shrink-0 mt-0.5">
              <User className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="font-semibold text-sm">Pedir nome e telefone na mesa</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Quando o cliente faz pedido pelo QR code da mesa, pede nome e telefone.<br/>
                Desativa se preferires pedidos anónimos (mais rápido, sem fricção).
              </p>
            </div>
          </div>
          <Switch
            checked={requireCustomerTable}
            onCheckedChange={setRequireCustomerTable}
            data-testid="require-customer-table-toggle"
          />
        </div>
        {!requireCustomerTable && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
            <p className="text-xs text-blue-700">
              <strong>Pedidos anónimos ativados.</strong> Clientes fazem pedidos sem identificação.
              O histórico de fidelização não será associado a este cliente.
            </p>
          </div>
        )}
      </div>

      {/* Reservas — confirmação automática */}
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
              <Bell className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-sm">Confirmação automática de reservas</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Quando ativo, reservas feitas pelo cliente são confirmadas de imediato e o cliente recebe WA de confirmação.<br/>
                Quando inativo, ficam pendentes até confirmares manualmente.
              </p>
            </div>
          </div>
          <Switch
            checked={autoConfirmReservations}
            onCheckedChange={setAutoConfirmReservations}
            data-testid="reservation-auto-confirm-toggle"
          />
        </div>
        {autoConfirmReservations ? (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
            <p className="text-xs text-emerald-700">
              <strong>Confirmação automática ativa.</strong> O cliente recebe WA de confirmação imediatamente ao reservar.
            </p>
          </div>
        ) : (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
            <p className="text-xs text-amber-700">
              <strong>Confirmação manual.</strong> Novas reservas ficam pendentes. Tens de confirmar no painel de Reservas.
            </p>
          </div>
        )}
      </div>

      {/* Flow summary */}
      <div className="bg-white border rounded-xl p-5">
        <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Fluxo de status dos pedidos
        </h4>
        <div className="flex items-center gap-2 flex-wrap text-xs">
          {paymentAtTable ? (
            <>
              <span className="px-2 py-1 rounded-full bg-gray-100 font-mono">pending</span>
              <span className="text-gray-400">→</span>
              <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-mono">in_preparation</span>
              <span className="text-gray-400">→</span>
              <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-mono">ready</span>
            </>
          ) : (
            <>
              <span className="px-2 py-1 rounded-full bg-gray-100 font-mono">pending</span>
              <span className="text-gray-400">→</span>
              <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-mono">pending_payment</span>
              <span className="text-gray-400">→ (após pagamento) →</span>
              <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-mono">in_preparation</span>
              <span className="text-gray-400">→</span>
              <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-mono">ready</span>
            </>
          )}
        </div>
      </div>

      <OperacoesNotificacoesSection
        reportEmail={reportEmail}
        setReportEmail={setReportEmail}
        whatsappNumber={whatsappNumber}
        setWhatsappNumber={setWhatsappNumber}
        autoClose={autoClose}
        setAutoClose={setAutoClose}
        autoCloseHora={autoCloseHora}
        setAutoCloseHora={setAutoCloseHora}
        isMidnightHour={isMidnightHour}
      />

      <Button
        onClick={handleSave}
        disabled={saving}
        className="w-full"
        data-testid="save-operacoes-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Guardar configurações
      </Button>
    </div>
  );
}
